import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface AirWaybillDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave?: (updatedData: any, changes?: any[]) => void
}

const AirWaybillDialog: React.FC<AirWaybillDialogProps> = ({ data, shipmentId, documentId, onSave }) => {
  // Define sections for Air Waybill
  const sections: DocumentSection[] = [
    {
      id: 'documentInfo',
      title: 'Document Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'AWB Number', path: 'awbNumber' },
        { type: 'field', label: 'Master AWB #', path: 'masterAwbNumber' },
        { type: 'field', label: 'House AWB #', path: 'houseAwbNumber' },
        { type: 'field', label: 'Booking Reference', path: 'bookingReference' },
        { type: 'field', label: 'Issue Date', path: 'issueDate' },
        { type: 'field', label: 'Execution Date', path: 'executionDate' },
        { type: 'field', label: 'Acceptance Date', path: 'acceptanceDate' },
        { type: 'field', label: 'Original Document', path: 'isOriginal' },
        { type: 'field', label: 'Copy Number', path: 'copyNumber' },
        { type: 'field', label: 'Total Copies', path: 'totalCopies' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Shipper',
          path: 'shipper',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
            { label: 'Account Number', path: 'accountNumber' },
          ],
        },
        {
          type: 'object',
          label: 'Consignee',
          path: 'consignee',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
            { label: 'Account Number', path: 'accountNumber' },
          ],
        },
        {
          type: 'object',
          label: 'Notify Party',
          path: 'notifyParty',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'flightInfo',
      title: 'Flight Information',
      layout: 'double',
      fields: [
        {
          type: 'object',
          label: 'Flight Details',
          path: 'flight',
          fields: [
            { label: 'Carrier Code', path: 'carrierCode' },
            { label: 'Flight Number', path: 'flightNumber' },
            { label: 'Flight Date', path: 'flightDate' },
            { label: 'Aircraft Type', path: 'aircraftType' },
          ],
        },
      ],
    },
    {
      id: 'airportsAndRouting',
      title: 'Airports and Routing',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Airports',
          path: '',
          fields: [
            { label: 'Departure Airport', path: 'departureAirport' },
            { label: 'Destination Airport', path: 'destinationAirport' },
          ],
        },
        {
          type: 'array',
          label: 'Routing',
          path: 'routing',
          columns: [
            { label: 'Airport', path: 'airport' },
            { label: 'Carrier', path: 'carrier' },
            { label: 'Flight Number', path: 'flightNumber' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'cargoSummary',
      title: 'Cargo Summary',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Basic Cargo Information',
          path: '',
          fields: [
            { label: 'Number of Pieces', path: 'pieces' },
            { label: 'General Cargo Description', path: 'cargoDescription' },
            { label: 'Handling Information', path: 'handlingInformation' },
          ],
        },
        {
          type: 'object',
          label: 'Total Weight and Volume',
          path: '',
          fields: [
            {
              label: 'Total Weight',
              path: 'totalWeight',
              render: value => (value ? `${value.value} ${value.unit}` : 'N/A'),
              complexType: 'weight',
            },
            {
              label: 'Total Volume',
              path: 'totalVolume',
              render: value => (value ? `${value.value} ${value.unit}` : 'N/A'),
              complexType: 'volume',
            },
          ],
        },
      ],
    },
    {
      id: 'cargoDetails',
      title: 'Detailed Cargo Manifest',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Individual Cargo Items',
          path: 'cargo',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'Pieces', path: 'pieces' },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
            {
              label: 'Volume',
              path: 'volume',
              render: value => (value ? `${value.value} ${value.unit}` : 'N/A'),
              complexType: 'volume',
            },
            {
              label: 'Dimensions',
              path: 'dimensions',
              render: value =>
                value ? `${value.length || '?'}×${value.width || '?'}×${value.height || '?'} ${value.unit}` : 'N/A',
              complexType: 'dimensions',
            },
            { label: 'Package Type', path: 'packageType' },
            { label: 'Marks & Numbers', path: 'marksAndNumbers' },
            { label: 'Commodity Code', path: 'commodityCode' },
            { label: 'Country of Origin', path: 'countryOfOrigin' },
            {
              label: 'Declared Value',
              path: 'value',
              render: value => (value ? `${value.amount} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
          ],
        },
      ],
    },
    {
      id: 'specialHandling',
      title: 'Special Handling',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Special Handling Codes',
          path: 'specialHandlingCodes',
          columns: [
            { label: 'Code', path: '.' }, // For string arrays, use "." to reference the string value directly
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'ulds',
      title: 'Unit Load Devices (ULDs)',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'ULD Information',
          path: 'ulds',
          columns: [
            { label: 'ULD Number', path: 'uldNumber' },
            { label: 'ULD Type', path: 'uldType' },
            { label: 'Contour Code', path: 'contourCode' },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'dangerousGoods',
      title: 'Dangerous Goods',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Dangerous Goods Information',
          path: 'dangerousGoods',
          fields: [
            { label: 'Contains Dangerous Goods', path: 'isDangerous' },
            { label: 'UN Number', path: 'unNumber' },
            { label: 'Proper Shipping Name', path: 'properShippingName' },
            { label: 'Hazard Class', path: 'hazardClass' },
            { label: 'Packing Group', path: 'packingGroup' },
            { label: 'Total Quantity', path: 'totalQuantity' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'chargesAndValues',
      title: 'Charges and Values',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Payment and Values',
          path: '',
          fields: [
            { label: 'Charges Code', path: 'chargesCode' },
            {
              label: 'Declared Value for Carriage',
              path: 'declaredValueForCarriage',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
            {
              label: 'Declared Value for Customs',
              path: 'declaredValueForCustoms',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
            {
              label: 'Insurance Value',
              path: 'insuranceValue',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
          ],
        },
        {
          type: 'object',
          label: 'Rate Information',
          path: 'rateCharge',
          fields: [
            { label: 'Rate Class', path: 'rateClass' },
            { label: 'Commodity Item Number', path: 'commodityItemNumber' },
            {
              label: 'Chargeable Weight',
              path: 'chargableWeight',
              render: value => (value ? `${value.value} ${value.unit}` : 'N/A'),
              complexType: 'weight',
            },
            {
              label: 'Rate',
              path: 'rate',
              render: value => (value ? `${value.value} ${value.currency} ${value.unit}` : 'N/A'),
              complexType: 'currency',
            },
            {
              label: 'Total Charge',
              path: 'totalCharge',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'otherCharges',
      title: 'Other Charges',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Additional Charges',
          path: 'otherCharges',
          columns: [
            { label: 'Code', path: 'code' },
            { label: 'Description', path: 'description' },
            {
              label: 'Amount',
              path: 'amount',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
            { label: 'Payment Type', path: 'paymentType' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'customsAndRegulatory',
      title: 'Customs and Regulatory',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Customs Information',
          path: '',
          fields: [
            {
              label: 'Customs Value',
              path: 'customsValue',
              render: value => (value ? `${value.value} ${value.currency}` : 'N/A'),
              complexType: 'currency',
            },
            { label: 'Country of Origin', path: 'countryOfOrigin' },
          ],
        },
        {
          type: 'object',
          label: 'Licenses',
          path: '',
          fields: [
            { label: 'Export License', path: 'exportLicense' },
            { label: 'Import License', path: 'importLicense' },
          ],
        },
      ],
    },
    {
      id: 'customerReferences',
      title: 'Customer References',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Customer Reference Numbers',
          path: 'customerReferences',
          columns: [
            { label: 'Reference', path: '.' }, // For string arrays
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'signaturesAndInstructions',
      title: 'Signatures and Instructions',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Signatures',
          path: '',
          fields: [
            { label: 'Carrier Signature', path: 'carrierSignature' },
            { label: 'Shipper Signature', path: 'shipperSignature' },
            { label: 'Agent Signature', path: 'agentSignature' },
            { label: 'Date of Signature', path: 'dateOfSignature' },
          ],
        },
        {
          type: 'object',
          label: 'Special Instructions',
          path: '',
          fields: [{ label: 'Special Instructions', path: 'specialInstructions' }],
        },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="Air Waybill"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default AirWaybillDialog
